import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/App";
import { UI, tr, COUNTRY_NAMES } from "@/i18n";
import { Lock, Check, ArrowLeft, ShieldCheck, FileText } from "lucide-react";
import { track } from "@/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, value }) => value ? (
  <div className="flex flex-col sm:flex-row sm:gap-3 py-1.5 border-b border-border/60 last:border-0">
    <span className="text-xs uppercase tracking-wide text-muted-foreground sm:w-56 shrink-0">{label}</span>
    <span className="text-sm text-navy-deep">{value}</span>
  </div>
) : null;

const PaidReport = ({ content, lang }) => {
  return (
    <div className="space-y-6">
      {content.sections.map((s, i) => {
        const k = s.knowledge || {};
        const pw = k.pathways ? Object.values(k.pathways)[0] : null;
        return (
          <div key={i} data-testid={`paid-section-${s.code}`} className="bg-white border border-border rounded-sm p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-2xl text-navy-deep">{s.flag} {tr(lang, COUNTRY_NAMES[s.code]) || s.name}</h3>
              <span className="text-sm bg-navy text-white rounded-full px-3 py-1">{s.overall}%</span>
            </div>
            {k.dataConfidence && <p className="mt-1 text-xs text-muted-foreground">{tr(lang, UI.d_confidence)}: <span className="text-navy font-medium">{k.dataConfidence}</span></p>}
            {pw && (
              <div className="mt-4">
                <h4 className="text-sm font-semibold text-navy-deep mb-1">{tr(lang, UI.d_pathways)}</h4>
                <Field label={tr(lang, UI.d_workrights)} value={pw.workRights} />
                <Field label={tr(lang, UI.d_financial)} value={pw.indicativeFinancial} />
                <Field label={tr(lang, UI.d_duration)} value={pw.duration} />
                <Field label={tr(lang, UI.d_renewal)} value={pw.renewal} />
                <Field label={tr(lang, UI.d_pr)} value={pw.prPathway} />
                <Field label={tr(lang, UI.d_citizenship)} value={pw.citizenshipPathway} />
                <Field label={tr(lang, UI.d_presence)} value={pw.physicalPresence} />
              </div>
            )}
            {k.familyRules && (
              <div className="mt-4"><h4 className="text-sm font-semibold text-navy-deep mb-1">{tr(lang, UI.d_family)}</h4>
                <p className="text-sm text-navy-deep">{k.familyRules}</p></div>
            )}
            {Array.isArray(k.sources) && k.sources.length > 0 && (
              <div className="mt-4"><h4 className="text-sm font-semibold text-navy-deep mb-1">{tr(lang, UI.d_sources)}</h4>
                <ul className="space-y-1">
                  {k.sources.map((src, j) => (
                    <li key={j} className="text-xs text-muted-foreground">
                      <a href={src.url} target="_blank" rel="noreferrer" className="text-navy underline">{src.org}</a> — {src.title} <span className="text-gold">({src.confidence})</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      })}
      <div className="bg-cream border border-border rounded-sm p-6">
        <h3 className="font-display text-xl text-navy-deep mb-2">{tr(lang, UI.d_next_steps)}</h3>
        <p className="text-sm text-navy-deep leading-relaxed">{tr(lang, UI.d_next_steps_text)}</p>
      </div>
    </div>
  );
};

const DetailedReport = () => {
  const { token } = useParams();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [content, setContent] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);

  const paid = data?.assessment?.reportPurchaseStatus === "paid";

  useEffect(() => {
    axios.get(`${API}/onematch/report/${token}`)
      .then((r) => {
        setData(r.data);
        if (r.data.assessment?.reportPurchaseStatus === "paid") {
          axios.get(`${API}/onematch/report/${token}/detailed-content`).then((c) => setContent(c.data)).catch(() => {});
        }
      })
      .catch(() => setNotFound(true));
  }, [token]);

  const checkout = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/payments/checkout`, {
        package_id: "detailed_report", origin_url: window.location.origin, reportToken: token,
      });
      track("checkout_started", { value: 19, currency: "EUR" });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(tr(lang, UI.error_generic)); setBusy(false); }
  };

  if (notFound) return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">{tr(lang, UI.report_not_found)}</div>;
  if (!data) return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-10 h-10 border-2 border-navy/20 border-t-navy rounded-full animate-spin" /></div>;

  const top3 = data.assessment.topMatches || [];
  const primary = top3[0] || {};
  const items = [UI.di_residency, UI.di_process, UI.di_family, UI.di_risks, UI.di_roadmap];

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14 om-fade-up">
      <button data-testid="detailed-back" onClick={() => navigate(`/onematch/report/${token}`)} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-navy mb-6">
        <ArrowLeft className="w-4 h-4" /> {tr(lang, UI.back_to_result)}
      </button>

      {paid ? (
        <>
          <div className="inline-flex items-center gap-2 text-xs font-semibold text-navy bg-gold/20 rounded-full px-3 py-1 mb-4">
            <ShieldCheck className="w-3.5 h-3.5" /> {tr(lang, UI.badge_paid)} · €19
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-navy-deep mb-6" data-testid="detailed-paid-head">{tr(lang, UI.detailed_paid_head)}</h1>
          {content ? <PaidReport content={content} lang={lang} /> :
            <div className="min-h-[30vh] flex items-center justify-center"><div className="w-8 h-8 border-2 border-navy/20 border-t-navy rounded-full animate-spin" /></div>}
        </>
      ) : (
        <>
          <div className="inline-flex items-center gap-2 text-xs font-semibold text-navy bg-gold/20 rounded-full px-3 py-1 mb-4">
            <Lock className="w-3.5 h-3.5" /> {tr(lang, UI.detailed_locked)}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-navy-deep">{tr(lang, UI.detailed_head)}</h1>
          <p className="mt-3 text-muted-foreground">{primary.flag} {tr(lang, COUNTRY_NAMES[primary.code]) || primary.name} — {primary.overall}%</p>

          <div className="mt-8 relative">
            <div className="bg-white border border-border rounded-sm p-6 sm:p-8">
              <h2 className="font-display text-2xl text-navy-deep mb-4">{tr(lang, UI.detailed_includes)}</h2>
              <ul className="space-y-3">
                {items.map((it, i) => (
                  <li key={i} data-testid={`detailed-item-${i}`} className="flex items-start gap-3 text-navy-deep">
                    <Check className="w-5 h-5 text-gold shrink-0 mt-0.5" /> <span>{tr(lang, it)}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 space-y-2" aria-hidden="true">
                {[...Array(4)].map((_, i) => (<div key={i} className="h-3 bg-cream rounded-full" style={{ width: `${90 - i * 12}%` }} />))}
              </div>
            </div>
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cream/40 to-cream rounded-sm pointer-events-none" />
          </div>

          <div className="mt-8 bg-navy-deep text-white rounded-sm p-6 sm:p-8 relative om-grain text-center">
            <FileText className="w-8 h-8 text-gold mx-auto mb-3" />
            <h2 className="font-display text-2xl sm:text-3xl">{tr(lang, UI.cta_unlock)}</h2>
            <p className="mt-3 text-white/70 text-sm leading-relaxed max-w-xl mx-auto">{tr(lang, UI.cta_unlock_sub)}</p>
            <button data-testid="detailed-checkout" onClick={checkout} disabled={busy}
              className="mt-6 inline-flex items-center gap-2 bg-gold text-navy-deep px-8 py-4 rounded-sm font-bold hover:bg-white transition-colors duration-300 disabled:opacity-50">
              {busy ? tr(lang, UI.saving) : tr(lang, UI.unlock_price)}
            </button>
          </div>
        </>
      )}

      <p className="mt-8 text-xs text-muted-foreground leading-relaxed">{tr(lang, UI.report_disclaimer)}</p>
    </div>
  );
};

export default DetailedReport;
